import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query'
import type { RawRequestArgs, ResponseOf } from '@tuyau/core/types'
import type { routes } from 'gozoki-backend/registry'
import { api } from '~/lib/tuyau'

type SimulatorListRoute = (typeof routes)['logistics.simulations.index']
/** Paramètres de `GET /logistics/simulations`, déduits du validateur du contrôleur. */
export type SimulatorListQuery = RawRequestArgs<SimulatorListRoute>['query']

/** Réponse de `GET /logistics/simulations`, déduite du contrôleur : aucun type recopié à la main. */
export type SimulatorListResponse = ResponseOf<SimulatorListRoute>
export type SimulatorListItem = SimulatorListResponse['simulations'][number]
export type SimulatorScreenStatus = SimulatorListItem['status']
export type SimulatorListActiveScenario = NonNullable<SimulatorListItem['activeScenario']>
/** Lieu d'un tracé : libellé et coordonnées du lieu du SCÉNARIO, pas du périmètre courant. */
export type SimulatorMapPathPlace = SimulatorListActiveScenario['paths'][number][number]

/** Boutique du réseau de `GET /logistics/network-stores` (couche « réseau » de la carte). */
export type SimulatorNetworkStore = ResponseOf<
  (typeof routes)['logistics.network_stores.index']
>['stores'][number]

/** Réponse de `GET /logistics/simulations/:id`, déduite du contrôleur. */
export type SimulationDetail = ResponseOf<(typeof routes)['logistics.simulations.show']>
export type SimulationDetailActiveScenario = NonNullable<SimulationDetail['activeScenario']>
export type SimulationDetailLatestRun = NonNullable<SimulationDetail['latestRun']>
export type RunDiagnostic = SimulationDetailLatestRun['diagnostics'][number]
/** Trajet complet du scénario actif (contrat solveur `Scenario`). */
export type ScenarioPayload = SimulationDetailActiveScenario['payload']
export type ScenarioSegment = ScenarioPayload['services'][number]['segments'][number]
export type ScenarioStop = ScenarioSegment['stops'][number]
export type ScenarioPlace = ScenarioSegment['origin']

/**
 * La liste relit le `payload` des scénarios actifs (lourd) : pas de relecture au simple retour
 * sur l'onglet du navigateur tant que les filtres n'ont pas changé.
 */
const LIST_STALE_TIME_MS = 60 * 1000

/**
 * Liste filtrée de `GET /logistics/simulations`, commune à la carte et au panneau liste : mêmes
 * paramètres, une seule requête (dédoublonnée par react-query). Les données précédentes restent
 * affichées pendant le rechargement qui suit un changement de filtre.
 */
export const useSimulatorListQuery = (query: SimulatorListQuery) =>
  useQuery(
    api.logistics.simulations.index.queryOptions(
      { query },
      { placeholderData: keepPreviousData, staleTime: LIST_STALE_TIME_MS },
    ),
  )

/** Les options changent seulement à la création ou à la suppression d'une simulation. */
const SELECTOR_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Options des sélecteurs « Départements » et « Cuisines centrales » : indépendantes des filtres,
 * lues à part sur une clé stable (sans filtre, une seule ligne de liste) et gardées en cache, au
 * lieu d'être recalculées à chaque appel de la liste filtrée.
 */
export const useSimulatorSelectorOptionsQuery = (enabled: boolean) =>
  useQuery(
    api.logistics.simulations.index.queryOptions(
      { query: { withSelectorOptions: true, pageSize: 1 } },
      {
        enabled,
        staleTime: SELECTOR_OPTIONS_STALE_TIME_MS,
        select: (response) => response.selectorOptions,
      },
    ),
  )

/** Le réseau change rarement (ouverture, fermeture, profil logistique saisi) : relu toutes les 5 min. */
const NETWORK_STORES_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Boutiques du réseau pour la carte, desservies ou non par une tournée : filtrées par le seul
 * département de la barre du haut (l'atelier et le jour ne concernent que les tournées).
 */
export const useSimulatorNetworkStoresQuery = (departmentCode: string | undefined) =>
  useQuery(
    api.logistics.networkStores.index.queryOptions(
      { query: { departmentCode } },
      {
        placeholderData: keepPreviousData,
        staleTime: NETWORK_STORES_STALE_TIME_MS,
        select: (response) => response.stores,
      },
    ),
  )

/**
 * Relit le détail (d'une simulation, ou de toutes : une bascule peut remplacer des tournées
 * d'autres simulations) ET la liste, qui garderait sinon l'ancien statut (fraîche 60 s).
 */
export const invalidateSimulations = (queryClient: QueryClient, simulationId?: number) =>
  Promise.all([
    queryClient.invalidateQueries(
      simulationId === undefined
        ? api.logistics.simulations.show.queryFilter()
        : api.logistics.simulations.show.queryFilter({ params: { id: simulationId } }),
    ),
    queryClient.invalidateQueries(api.logistics.simulations.index.queryFilter()),
  ])
